import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { Encrypt } from "../utils/encryption";
import { mediaApi, messagingApi } from "../config/api";

/**
 * Zibuzz_Messaging: encrypted payload uses snake_case keys; `operationType` is top-level.
 * @param {string} token - The session token.
 * @param {{ onAuthError?: () => void }} options — called on 401 / invalid session so UI can return to login.
 * @returns {object|null} The messaging client or null if WhatsApp is not connected.
 */
export function createMessagingClient(token, options = {}) {
  if (!token) throw new Error("Missing session token");

  let userProfile = {};
  try {
    const decoded = jwtDecode(token);
    userProfile = decoded?.UserProfile || {};
    if (!userProfile.wabaId) {
      return null;
    }
  } catch (e) {
    return null;
  }

  const { onAuthError } = options;

  const api = axios.create();

  api.interceptors.response.use(
    (res) => res,
    (err) => {
      const status = err.response?.status;
      const msg = String(err.response?.data?.message || err.message || "");
      if (
        status === 401 ||
        /invalid or expired session|unauthorized|jwt expired|token expired/i.test(
          msg
        )
      ) {
        onAuthError?.();
      }
      return Promise.reject(err);
    }
  );

  const post = (operationType, payload = {}, path = "", baseApi = messagingApi) =>
    api
      .post(
        `${baseApi}${path}`,
        { ...Encrypt({ token, ...payload }), operationType },
        {
          headers: {
            "Content-Type": "application/json",
            "X-Operation-Type": operationType,
            "X-Waba-Id": userProfile.wabaId || "",
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((r) => r.data);

  return {
    listThreads() {
      return post("messagingListChatThreads", {});
    },

    queryMessages(contact_number, opts = {}) {
      const payload = {
        contact_number,
        limit: opts.limit ?? 50,
        exclusive_start_key: opts.exclusive_start_key,
        scan_index_forward: opts.scan_index_forward ?? true,
      };
      if (opts.clear_unread === true) {
        payload.clear_unread = true;
      }
      return post("messagingQueryMessages", payload);
    },

    sendReply(contact_number, text, contact_name = "", context = null) {
      return post("messagingSendWhatsApp", {
        contact_number,
        text,
        contact_name,
        context,
      });
    },

    getTemplates(waba_id) {
      return post("getTemplates", { waba_id }, "/templates");
    },

    getTemplateLibrary(params = {}) {
      return post("getTemplateLibrary", params);
    },

    createTemplate(waba_id, template) {
      return post("createTemplate", { waba_id, template });
    },

    updateTemplate(template_id, template) {
      return post("updateTemplate", { template_id, template });
    },

    deleteTemplate(waba_id, name) {
      return post("deleteTemplate", { waba_id, name });
    },

    sendTemplate(contact_number, template, contact_name = "") {
      return post("sendTemplate", {
        contact_number,
        contact_name,
        name: template.name,
        language_code: template.language_code || template.code,
        components: template.components || [],
        preview_text: template.preview_text || template.text || "",
        template_name: template.template_name || template.name,
      });
    },

    sendReaction(contact_number, message_id, emoji, contact_name = "") {
      return post("sendReaction", {
        contact_number,
        contact_name,
        message_id,
        emoji,
      });
    },

    sendMedia(contact_number, type, media_id, opts = {}) {
      return post("sendMedia", {
        contact_number,
        contact_name: opts.contact_name || "",
        type,
        media_id,
        cdn_url: opts.cdn_url,
        s3_key: opts.s3_key,
        mime_type: opts.mime_type,
        file_size: opts.file_size,
        file_name: opts.file_name,
        media: opts.media,
        caption: opts.caption,
      });
    },

    createMediaUpload(contact_number, file, type) {
      return post(
        "mediaCreateUpload",
        {
          contact_number,
          media_type: type,
          mime_type: file.type,
          file_size: file.size,
          file_name: file.name,
        },
        "",
        mediaApi
      );
    },

    deleteMediaUpload(s3_key) {
      return post("mediaDeleteUpload", { s3_key }, "", mediaApi);
    },

  };
}
